import { useCallback, useEffect, useRef, useState } from "react";
import Quill from "quill";
import { QuillBinding } from "y-quill";
import { rasteGroesse, rasteVerschieben } from "../hilfen/einrasten";
import { bildSatz } from "../hilfen/folienmodell";
import { geschuetzteAdresse } from "../dienst/server";

/*
  Die Bühne — eine Folie zum Bearbeiten.

  ---------------------------------------------------------------
  Warum die Schrift in `cqh` gerechnet wird
  ---------------------------------------------------------------

  Dieselbe Folie erscheint klein im Streifen, groß auf der Bühne und
  bildschirmfüllend im Vorführmodus. Eine Schriftgröße in Pixeln passte
  immer nur zu einer davon.

  `cqh` ist ein Prozentsatz der Höhe des umgebenden Kastens. Setzt man die
  Folie als solchen Kasten, wächst die Schrift mit ihr — ohne eine einzige
  Zeile JavaScript und ohne Messen. Genau das Messen war beim ersten Editor
  die Fehlerquelle: Im versteckten Fenster kamen falsche Werte zurück, und
  einzeilige Absätze wurden tausend Pixel hoch.

  ---------------------------------------------------------------
  Ziehen und Tippen im selben Kasten
  ---------------------------------------------------------------

  Ein Textkasten enthält einen Editor. Würde der ganze Kasten am Finger
  hängen, ließe sich die Schreibmarke nicht mehr setzen — und würde er es
  nicht, käme man nie zum Verschieben.

  Deshalb ist beides getrennt: Im Text tippt man wie überall. Verschoben
  wird über einen eigenen Griff über dem Kasten, der erscheint, sobald der
  Kasten gewählt ist. Der Griff ist ein Knopf und damit auch mit der
  Tastatur erreichbar — dort verschieben die Pfeiltasten, mit Umschalt
  ändern sie die Größe. Ohne diesen Weg wäre ausgeschlossen, wer zittrige
  Hände hat; die Regel gilt seit dem Aufgabenbrett.
*/

/*
  Erst halten, dann ziehen — dieselbe Schwelle wie beim Aufgabenbrett.
  Ohne sie nähme jede Wischbewegung einen Kasten mit, und die Seite ließe
  sich nicht mehr scrollen.
*/
const HALTEN_MS = 250;

/* Ein Tastendruck bewegt um ein Prozent, mit Umschalt um fünf. */
const SCHRITT = 1;
const GROSSER_SCHRITT = 5;

/*
  Wie viel Platz der Griff über dem Kasten braucht, in Prozent der
  Folienhöhe. Rund zwei Zeilen — darunter rutscht er nach innen.
*/
const GRIFF_PLATZ = 8;

export default function Folienbuehne({
  folie,
  gewaehlt,
  aufWahl,
  aufSchreibstelle,
  aufBindung,
  aufQuill,
  zugang,
  nurLesen = false,
  klein = false,
}) {
  const flaecheRef = useRef(null);
  const [hilfslinien, setzeHilfslinien] = useState({ senkrecht: null, waagerecht: null });
  const kaesten = folie ? folie.get("kaesten") : null;

  /* Die anderen Kästen — an ihnen rastet der gezogene ein. */
  const fremdeFuer = useCallback(
    (id) =>
      (kaesten ? kaesten.toArray() : [])
        .filter((k) => k.get("id") !== id)
        .map((k) => ({
          x: k.get("x"),
          y: k.get("y"),
          breite: k.get("breite"),
          hoehe: k.get("hoehe"),
        })),
    [kaesten]
  );

  if (!folie || !kaesten) return <div className="folie folie--leer" />;

  return (
    <div
      className={`folie ${klein ? "folie--klein" : ""}`}
      ref={flaecheRef}
      /*
        Auf die freie Fläche tippen hebt die Auswahl auf. Ohne das bliebe
        ein Kasten dauerhaft mit Griff und vier Ecken behängt, und man
        käme nicht wieder in den ruhigen Blick auf die Folie.

        Geprüft wird `target === currentTarget`: Nur ein Tippen auf die
        Folie SELBST zählt. Ein Tippen auf einen Kasten steigt zwar bis
        hierher auf, hat aber ein anderes Ziel — sonst hätte jede Auswahl
        sich im selben Atemzug wieder aufgehoben.
      */
      onPointerDown={(e) => {
        if (aufWahl && e.target === e.currentTarget) aufWahl(null);
      }}
    >
      {kaesten.toArray().map((kasten) => (
        <Kasten
          key={kasten.get("id")}
          kasten={kasten}
          flaecheRef={flaecheRef}
          fremde={fremdeFuer(kasten.get("id"))}
          gewaehlt={gewaehlt === kasten.get("id")}
          aufWahl={aufWahl}
          aufHilfslinien={setzeHilfslinien}
          aufSchreibstelle={aufSchreibstelle}
          aufBindung={aufBindung}
          aufQuill={aufQuill}
          zugang={zugang}
          nurLesen={nurLesen || klein}
          schlicht={klein}
        />
      ))}

      {/*
        Die Hilfslinien. Ohne sie weiß niemand, WARUM der Kasten gerade
        gesprungen ist — es sähe nach einem Fehler aus statt nach Hilfe.
      */}
      {hilfslinien.senkrecht !== null && (
        <div className="hilfslinie hilfslinie--senkrecht" style={{ left: `${hilfslinien.senkrecht}%` }} />
      )}
      {hilfslinien.waagerecht !== null && (
        <div className="hilfslinie hilfslinie--waagerecht" style={{ top: `${hilfslinien.waagerecht}%` }} />
      )}
    </div>
  );
}

const ECKEN = ["oben-links", "oben-rechts", "unten-links", "unten-rechts"];

function Kasten({
  kasten,
  flaecheRef,
  fremde,
  gewaehlt,
  aufWahl,
  aufHilfslinien,
  aufSchreibstelle,
  aufBindung,
  aufQuill,
  zugang,
  nurLesen,
  schlicht,
}) {
  const behaelterRef = useRef(null);
  const bindungRef = useRef(null);
  const quillRef = useRef(null);
  const halter = useRef(null);
  const zieht = useRef(null);
  const [, neuZeichnen] = useState(0);

  const art = kasten.get("art");
  const text = kasten.get("text");

  /* Maße können sich von außen ändern — dann neu zeichnen. */
  useEffect(() => {
    const beiAenderung = () => neuZeichnen((n) => n + 1);
    kasten.observe(beiAenderung);
    return () => kasten.unobserve(beiAenderung);
  }, [kasten]);

  /*
    Ein Quill je Textkasten — und beim Verlassen sauber abgebaut.

    Ohne das Abbauen bliebe bei jedem Folienwechsel eine Bindung an Yjs
    zurück. Nach zwanzig Folien wären es zwanzig, die alle bei jeder
    Änderung mitrechnen.
  */
  useEffect(() => {
    if (art !== "text" || !behaelterRef.current || nurLesen || quillRef.current) return undefined;

    /*
      `snow` ohne eigene Leiste — nicht `bubble`.

      Die Leiste steht über der Bühne, genau wie im Textdokument; hier wird
      also nur der Schreibbereich gebraucht. `snow` und nicht `bubble`,
      weil Quills Größenklassen (`ql-size-large` und die anderen) im
      Stilblatt an `.ql-snow` hängen — mit `bubble` bliebe die Schriftgröße
      aus der Leiste wirkungslos. Den Rahmen, den `snow` sonst zeichnet,
      nimmt das Stilblatt schon weg.
    */
    const quill = new Quill(behaelterRef.current, {
      theme: "snow",
      placeholder: "Text …",
      modules: {
        toolbar: false,
        /*
          Quills eigenes Rückgängig wird abgeschaltet, sonst wirkt Strg+Z
          zweimal: einmal in diesem Kasten und einmal im Verwalter über der
          ganzen Präsentation. Quills Fassung kennt außerdem nur ihren
          eigenen Kasten — sie wüsste nichts von einem verschobenen Kasten
          und nähme bei mehreren Leuten auch fremde Sätze zurück.
        */
        history: { delay: 0, maxStack: 0, userOnly: true },
      },
    });
    quillRef.current = quill;
    const bindung = new QuillBinding(text, quill);
    bindungRef.current = bindung;

    /*
      Rückgängig darf nur zurücknehmen, was aus DIESEM Browser kam. Die
      Bindung ist der Absender dieser Änderungen, also muss sie beim
      Rückgängig-Verwalter angemeldet sein — sonst bliebe getippter Text
      von Strg+Z unberührt, während verschobene Kästen zurückspringen.
    */
    if (aufBindung) aufBindung(bindung);

    /*
      Sich beim Elternteil bekannt machen. Ein Kommentar am Rand trägt den
      Knopf „Zur Stelle" — der braucht genau dieses Quill, um die
      Textstelle auszuwählen. Ohne die Anmeldung führte der Knopf nur zum
      Kasten und nicht zum Wort.
    */
    if (aufQuill) aufQuill(kasten.get("id"), quill);

    /*
      Wer zuletzt geschrieben hat, bekommt die Leiste. Gemeldet wird nur
      beim Hineingehen, nicht beim Verlassen: Ein Druck auf „Fett" nimmt
      dem Kasten kurz den Fokus, und die Leiste soll dabei nicht ins Leere
      greifen.
    */
    const beiAuswahl = (bereich) => {
      if (bereich && aufSchreibstelle) aufSchreibstelle({ quill, kasten, bereich });
    };
    quill.on("selection-change", beiAuswahl);

    return () => {
      quill.off("selection-change", beiAuswahl);
      if (aufBindung) aufBindung(bindung, true);
      if (aufQuill) aufQuill(kasten.get("id"), null);
      bindung.destroy();
      bindungRef.current = null;
      quillRef.current = null;
      if (aufSchreibstelle) aufSchreibstelle({ quill: null, weg: quill });
    };
  }, [art, text, nurLesen, aufBindung, aufSchreibstelle, aufQuill, kasten]);

  /* ---------- Ziehen ---------- */

  /* Pixel des Zeigers in Prozent der Folie. */
  function alsProzent(e) {
    const flaeche = flaecheRef.current;
    if (!flaeche) return { x: 0, y: 0 };
    const r = flaeche.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 };
  }

  function beginne(e, was) {
    if (nurLesen) return;
    const start = alsProzent(e);
    const griff = e.currentTarget;
    const zeiger = e.pointerId;

    const anfang = {
      was,
      start,
      x: kasten.get("x"),
      y: kasten.get("y"),
      breite: kasten.get("breite"),
      hoehe: kasten.get("hoehe"),
    };

    halter.current = setTimeout(() => {
      zieht.current = anfang;
      /*
        Den Zeiger ERST JETZT einfangen. Beim Aufsetzen eingefangen,
        leitet er auch das Klick-Ereignis um — dann bekäme der Knopf im
        Kasten es nie. Am Aufgabenbrett hatte genau das den ganzen
        Knopfweg unbrauchbar gemacht.
      */
      try {
        griff.setPointerCapture(zeiger);
      } catch {
        /* Zeiger schon los — dann gibt es nichts einzufangen. */
      }
    }, HALTEN_MS);
  }

  function beiBewegung(e) {
    if (!zieht.current) {
      /* Wer wischt statt zu halten, will scrollen — Halten abbrechen. */
      if (halter.current) {
        clearTimeout(halter.current);
        halter.current = null;
      }
      return;
    }

    const jetzt = alsProzent(e);
    const z = zieht.current;
    const dx = jetzt.x - z.start.x;
    const dy = jetzt.y - z.start.y;

    if (z.was === "verschieben") {
      const roh = { x: z.x + dx, y: z.y + dy, breite: z.breite, hoehe: z.hoehe };
      const gerastet = rasteVerschieben(roh, fremde);
      kasten.doc.transact(() => {
        kasten.set("x", gerastet.x);
        kasten.set("y", gerastet.y);
      });
      aufHilfslinien(gerastet.hilfslinien);
      return;
    }

    /* Größe: die bewegte Ecke wandert, die gegenüberliegende bleibt. */
    const roh = { x: z.x, y: z.y, breite: z.breite, hoehe: z.hoehe };
    if (z.was.includes("links")) {
      roh.x = z.x + dx;
      roh.breite = z.breite - dx;
    } else {
      roh.breite = z.breite + dx;
    }
    if (z.was.includes("oben")) {
      roh.y = z.y + dy;
      roh.hoehe = z.hoehe - dy;
    } else {
      roh.hoehe = z.hoehe + dy;
    }

    const gerastet = rasteGroesse(roh, fremde, z.was);
    kasten.doc.transact(() => {
      kasten.set("x", gerastet.x);
      kasten.set("y", gerastet.y);
      kasten.set("breite", gerastet.breite);
      kasten.set("hoehe", gerastet.hoehe);
    });
    /*
      Auch beim Vergrößern zeigen, woran die Kante gefangen hat. Ohne das
      sähe man den Sprung, aber nicht den Grund — beim Verschieben half die
      Linie, beim Vergrößern fehlte sie.
    */
    aufHilfslinien(gerastet.hilfslinien);
  }

  function beiEnde() {
    if (halter.current) clearTimeout(halter.current);
    halter.current = null;
    zieht.current = null;
    aufHilfslinien({ senkrecht: null, waagerecht: null });
  }

  /* ---------- Der Weg über die Tastatur ---------- */

  function beiTaste(e) {
    const richtungen = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const richtung = richtungen[e.key];
    if (!richtung) return;
    e.preventDefault();

    const weite = e.altKey ? GROSSER_SCHRITT : SCHRITT;
    const [rx, ry] = richtung;

    kasten.doc.transact(() => {
      if (e.shiftKey) {
        /* Umschalt ändert die Größe statt der Lage. */
        kasten.set("breite", Math.max(5, Math.min(kasten.get("breite") + rx * weite, 100 - kasten.get("x"))));
        kasten.set("hoehe", Math.max(5, Math.min(kasten.get("hoehe") + ry * weite, 100 - kasten.get("y"))));
      } else {
        kasten.set("x", Math.max(0, Math.min(kasten.get("x") + rx * weite, 100 - kasten.get("breite"))));
        kasten.set("y", Math.max(0, Math.min(kasten.get("y") + ry * weite, 100 - kasten.get("hoehe"))));
      }
    });
  }

  const lage = {
    left: `${kasten.get("x")}%`,
    top: `${kasten.get("y")}%`,
    width: `${kasten.get("breite")}%`,
    height: `${kasten.get("hoehe")}%`,
    fontSize: `${kasten.get("groesse")}cqh`,
  };

  /*
    Ohne Bearbeitung — in zwei Ausprägungen.

    `schlicht` ist der Streifen links: dort ist eine Folie zwei Zentimeter
    breit, und ein Quill je Kasten hieße bei zwanzig Folien sechzig Editoren
    nur für die Vorschau. Da genügt der nackte Text.

    Ohne `schlicht` — Vorführmodus und Druck — muss die Folie dagegen genau
    so aussehen wie beim Bearbeiten. Fett, Ausrichtung und Schriftgröße sind
    das halbe Ergebnis; eine Folie, die auf dem Beamer anders aussieht als
    im Editor, wäre der schlimmste Zeitpunkt für eine Überraschung. Deshalb
    dort ein Quill, nur eben ohne Schreibrecht.
  */
  if (nurLesen) {
    return (
      <div
        className={`folienkasten ${schlicht ? "folienkasten--still" : ""}`}
        style={lage}
      >
        {art === "bild" ? (
          <Bild kasten={kasten} zugang={zugang} />
        ) : schlicht ? (
          text ? text.toString() : ""
        ) : (
          <Gelesen text={text} />
        )}
      </div>
    );
  }

  return (
    <div
      className={`folienkasten ${gewaehlt ? "folienkasten--gewaehlt" : ""}`}
      style={lage}
      onPointerDown={() => aufWahl && aufWahl(kasten.get("id"))}
    >
      {art === "bild" ? (
        <Bild kasten={kasten} zugang={zugang} />
      ) : (
        <div ref={behaelterRef} className="folienkasten__text" />
      )}

      {gewaehlt && (
        <>
          {/*
            Der Griff. Ein Knopf, damit die Tastatur ihn erreicht — dort
            verschieben die Pfeiltasten, mit Umschalt ändern sie die Größe,
            mit Alt in größeren Schritten.
          */}
          <button
            className={`folienkasten__griff ${kasten.get("y") < GRIFF_PLATZ ? "folienkasten__griff--innen" : ""}`}
            title="Halten und ziehen zum Verschieben · Pfeiltasten bewegen, mit Umschalt Größe"
            aria-label="Kasten verschieben"
            onPointerDown={(e) => beginne(e, "verschieben")}
            onPointerMove={beiBewegung}
            onPointerUp={beiEnde}
            onPointerCancel={beiEnde}
            onKeyDown={beiTaste}
          >
            ⠿
          </button>

          {ECKEN.map((ecke) => (
            <button
              key={ecke}
              className={`folienkasten__ecke folienkasten__ecke--${ecke}`}
              aria-label={`Größe ändern, Ecke ${ecke.replace("-", " ")}`}
              title="Halten und ziehen zum Vergrößern"
              onPointerDown={(e) => beginne(e, ecke)}
              onPointerMove={beiBewegung}
              onPointerUp={beiEnde}
              onPointerCancel={beiEnde}
              onKeyDown={beiTaste}
            />
          ))}
        </>
      )}
    </div>
  );
}

/*
  Ein Bild auf der Folie.

  Hochgeladene Dateien sind geschützt und kommen nur mit einem kurzlebigen
  Token heraus. Das liefert `useDateiZugang` weiter oben; hier wird es nur
  angehängt.

  Die drei Zustände sind alle sichtbar gemacht, weil ein leerer Kasten auf
  einer Folie nicht von einem absichtlich leeren zu unterscheiden wäre:
  noch kein Token, Datei nicht mehr da, geladen.
*/
function Bild({ kasten, zugang }) {
  const [gescheitert, setzeGescheitert] = useState(false);
  const satz = bildSatz(kasten);
  const adresse = geschuetzteAdresse(zugang, satz, "datei");

  useEffect(() => {
    setzeGescheitert(false);
  }, [adresse]);

  if (!satz) return <div className="folienkasten__bildplatz">Kein Bild hinterlegt</div>;
  if (gescheitert)
    return <div className="folienkasten__bildplatz">Bild nicht mehr vorhanden</div>;
  if (!adresse) return <div className="folienkasten__bildplatz">Wird geladen …</div>;

  return (
    <img
      className="folienkasten__bild"
      src={adresse}
      alt=""
      draggable={false}
      onError={() => setzeGescheitert(true)}
    />
  );
}

/*
  Ein Textkasten zum Ansehen — Vorführmodus und Druck.

  Es ist derselbe Editor wie beim Bearbeiten, nur ohne Schreibrecht. Das
  klingt nach Verschwendung, ist aber der einzige Weg, bei dem die Folie
  wirklich gleich aussieht: Quills Formatierung steckt in seinen eigenen
  Klassen (`ql-align-center`, `ql-size-large`), und die entstehen nur, wenn
  Quill den Inhalt selbst zeichnet.

  Gebunden wird NICHT an Yjs — eine Bindung wäre ein Schreibweg. Stattdessen
  wird der Inhalt gesetzt und bei jeder Änderung neu gesetzt. Wer während
  der Vorführung von einem anderen Gerät etwas ändert, sieht es also auch
  auf dem Beamer.
*/
function Gelesen({ text }) {
  const behaelterRef = useRef(null);

  useEffect(() => {
    if (!behaelterRef.current || !text) return undefined;

    const quill = new Quill(behaelterRef.current, {
      theme: "snow",
      readOnly: true,
      modules: { toolbar: false, history: { delay: 0, maxStack: 0, userOnly: true } },
    });

    const uebernimm = () => {
      /*
        „silent" — sonst meldete Quill die Übernahme als Änderung des
        Benutzers zurück. Hier ändert niemand etwas, hier wird angezeigt.
      */
      quill.setContents(text.toDelta(), "silent");
    };

    uebernimm();
    text.observe(uebernimm);
    return () => text.unobserve(uebernimm);
  }, [text]);

  return <div ref={behaelterRef} className="folienkasten__text" />;
}
